/**
 * Shared classifier: is an error a *transient loss of connectivity* (offline,
 * dropped socket, aborted or timed-out request) rather than a real server-side
 * failure?
 *
 * Used wherever a self-healing network blip must be told apart from a genuine
 * error so both react the same way — breadcrumb, not a page:
 * - the Realtime resync logger ({@link logResyncFailure}),
 * - the telephony Twilio-token refresh handler (a "Failed to fetch" while the
 *   user's network is down must not read as a softphone bug).
 */

/** Best-effort message extraction from Error, error-like objects, or anything. */
export function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message ?? '');
  }
  return String(err ?? '');
}

/**
 * Cross-browser fingerprints of "the request never reached the server". Matched
 * case-insensitively as substrings of the error message.
 * - Chrome/Edge: `Failed to fetch`   - Firefox: `NetworkError when attempting…`
 * - Safari: `Load failed` / `The network connection was lost` / `…offline`
 * - Aborted socket / timeout on either.
 */
const TRANSIENT_NETWORK_PATTERNS = [
  'failed to fetch',
  'load failed',
  'networkerror',
  'network request failed',
  'network connection was lost',
  'connection appears to be offline',
  'aborted',
  'timeout',
] as const;

/**
 * An answer that carries a SQL or PostgREST code (`57014` statement timeout, `42501`, `PGRST116`…)
 * came from the server: a reconnect will not fix it, whatever its message says. A request that never
 * reached the server comes back with an empty code.
 */
function hasServerCode(err: unknown): boolean {
  if (!err || typeof err !== 'object' || !('code' in err)) return false;
  const code = (err as { code: unknown }).code;
  return typeof code === 'string' && code !== '';
}

/** True when the failure is a transient loss of connectivity, not a server-side error. */
export function isTransientNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (hasServerCode(err)) return false;
  const msg = errMessage(err).toLowerCase();
  return TRANSIENT_NETWORK_PATTERNS.some((pattern) => msg.includes(pattern));
}
